/* eslint @typescript-eslint/no-explicit-any: 0 */
import dayjs from "dayjs"
import locale from "./locale"
import {
  DateTimeFormatType,
  MomentFormatType,
  NumberFormatType,
  StringFormatType,
} from "./types"

function formatRelativeTime(value: Date) {
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(
    locale.getCurrentHierarchy()
  )
  const timeDelta = Date.now() - value.getTime()
  const minute = 60000
  const hour = minute * 60
  const day = hour * 24
  if (timeDelta <= hour) {
    const minutes = Math.ceil(timeDelta / minute)
    return relativeTimeFormatter.format(-minutes, "minute")
  }
  if (timeDelta <= day) {
    const hours = Math.floor(timeDelta / hour)
    return relativeTimeFormatter.format(-hours, "hour")
  }
  const days = Math.floor(timeDelta / day)
  return relativeTimeFormatter.format(-days, "day")
}

function formatDateTime(
  value: Date,
  formatType?: DateTimeFormatType | MomentFormatType
) {
  if (formatType === "relative") {
    return formatRelativeTime(value)
  } else {
    const {
      date,
      time,
      format: momentFormat,
    } = formatType as Record<string, string>

    if (typeof momentFormat === "string") {
      return dayjs(value).format(momentFormat)
    } else {
      const loc = locale.getCurrentHierarchy();
      const options: Intl.DateTimeFormatOptions = {};
      if (date) {
        if (date === 'short') {
          options.year = 'numeric';
          options.month = '2-digit';
          options.day = '2-digit';
        } else {
          options.dateStyle = date as Intl.DateTimeFormatOptions['dateStyle'];
        }
      }
      if (time) {
        if (time === 'short') {
          options.hour = '2-digit';
          options.minute = '2-digit';
        } else {
          options.timeStyle = time as Intl.DateTimeFormatOptions['timeStyle'];
        }
      }
      return new Intl.DateTimeFormat(loc, options as any).format(value);
    }
  }
}

/**
 * Maps language codes to proper locale codes for number formatting
 * This ensures proper decimal and thousands separators according to locale standards
 */
function getNumberFormatLocale(localeHierarchy: string | string[]): string | string[] {
	const mapLanguageToLocale = (lang: string): string => {
		// If already a full locale code (e.g., 'da-DK'), return as is
		if (lang.includes('-')) {
			return lang
		}
		// Map language codes to proper locale codes
		switch (lang) {
			case 'da':
				return 'da-DK' // Danish - uses comma as decimal separator
			case 'en':
				return 'en-US' // English (US)
			case 'sv':
				return 'sv-SE' // Swedish
			case 'no':
				return 'nb-NO' // Norwegian Bokmål
			default:
				return lang
		}
	}

	if (Array.isArray(localeHierarchy)) {
		return localeHierarchy.map(mapLanguageToLocale)
	}
	return mapLanguageToLocale(localeHierarchy)
}

function formatNumber(value: number, formatType?: NumberFormatType) {
	const minimumFractionDigits = formatType?.decimals instanceof Array ? formatType.decimals[0] : formatType?.decimals
	const maximumFractionDigits = formatType?.decimals instanceof Array ? formatType.decimals[1] : formatType?.decimals

	const localeHierarchy = locale.getCurrentHierarchy()
	const numberFormatLocale = getNumberFormatLocale(localeHierarchy)

	// Use proper locale for number formatting to ensure correct decimal/thousands separators
	const formattedNumber = new Intl.NumberFormat(numberFormatLocale, {
		minimumFractionDigits,
		maximumFractionDigits,
	}).format(value)

	// Append currency if specified
	return formatType?.currency ? `${formattedNumber} ${formatType.currency}` : formattedNumber
}

function formatString(value: string, formatType?: StringFormatType) {
  if (formatType === "lowercase") {
    return value.toLowerCase()
  } else if (formatType === "uppercase") {
    return value.toUpperCase()
  } else if (formatType === "capitalize") {
    return value.length > 0
      ? `${value[0].toUpperCase()}${value.slice(1)}`
      : value
  } else {
    return value
  }
}

function format(value: Date, formatType?: DateTimeFormatType): string
function format(value: Date, formatType?: MomentFormatType): string
function format(value: number, formatType?: NumberFormatType): string
function format(value: string, formatType?: StringFormatType): string
function format(value: unknown, formatType?: unknown): string {
  if (value instanceof Date) {
    return formatDateTime(
      value,
      formatType as DateTimeFormatType | MomentFormatType
    )
  } else if (typeof value === "number") {
    return formatNumber(value, formatType as NumberFormatType)
  } else if (typeof value === "string") {
    return formatString(value, formatType as StringFormatType)
  } else {
    return String(value)
  }
}

export default format
